// Components/Services/HOACommercial/Overview.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiShield, FiLayers, FiTool } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const serviceTypes = [
  {
    icon: <FiShield className="w-5 h-5 text-[#212529]" />,
    title: "Building Envelope Rehabilitation",
    desc: "Waterproofing, exterior cladding, balcony and deck repair, window replacement, and full façade restoration for HOA communities.",
  },
  {
    icon: <FiLayers className="w-5 h-5 text-[#212529]" />,
    title: "Common Area Reconstruction",
    desc: "Clubhouses, lobbies, walkways, parking structures, and shared spaces rebuilt or renovated to HOA board specifications.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "Light Commercial Tenant Improvements",
    desc: "Office build-outs, retail renovations, and commercial interior improvements managed from permit to punch-list.",
  },
];

const stats = [
  { number: "$1M+",  label: "Project Capacity"         },
  { number: "100%",  label: "Licensed & Bonded"         },
  { number: "A–Z",   label: "Project Management"        },
  { number: "3rd",   label: "Generation Contractors"    },
];

const HOACommercialOverview = () => {
  return (
    <section className="bg-white">

      {/* ── IMAGE + TEXT ── */}
      <div className="max-w-6xl mx-auto px-6 py-14">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left — image */}
          <motion.div
            variants={fadeLeft}
            className="relative rounded-2xl overflow-hidden shadow-xl min-h-[380px] group"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <img
              src="/Images/ADUs.png"
              alt="HOA and Commercial Projects by Ashlar Building Group"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <span className="bg-white/90 backdrop-blur-sm text-[#212529] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                HOA & Commercial
              </span>
            </div>
          </motion.div>

          {/* Right — text */}
          <motion.div variants={fadeRight} className="flex flex-col justify-center">
            <p className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
              What We Handle
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-[#212529] leading-tight mb-4">
              Complex Projects.<br />Delivered Without Drama.
            </h3>
            <p className="text-[#495057] text-sm leading-relaxed mb-3">
              HOA and commercial projects demand a different level of coordination, documentation, and accountability than standard residential work. Boards, property managers, and commercial tenants need a contractor who shows up, communicates clearly, and doesn't disappear when things get complicated.
            </p>
            <p className="text-[#495057] text-sm leading-relaxed mb-5">
              Ashlar has the licensing, bonding, and operational systems to handle high-value community and commercial projects — from multi-unit building envelope work to full commercial tenant improvements — with the same craftsmanship and accountability we bring to every project.
            </p>

            <div className="space-y-3">
              {serviceTypes.map((type, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-[#f8f9fa] rounded-xl px-4 py-3 border border-[#dee2e6] cursor-default"
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {type.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{type.title}</p>
                    <p className="text-[#6c757d] text-xs mt-0.5 leading-relaxed">{type.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="pb-14 px-4 md:px-6">
        <motion.div
          className="max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            variants={fadeUp}
            className="bg-[#343a40] rounded-2xl shadow-lg px-8 py-8 md:px-12 md:py-10"
          >
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="lg:w-[26%] text-center flex flex-col items-center flex-shrink-0">
                <p className="text-[#dee2e6] text-base font-medium whitespace-nowrap">Built for boards and</p>
                <p className="text-xl md:text-2xl font-bold text-[#f8f9fa] whitespace-nowrap">Property Managers</p>
              </div>
              <div className="hidden lg:block w-px h-14 bg-[#495057] flex-shrink-0" />
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    whileHover={{ scale: 1.1, y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="flex flex-col items-center text-center cursor-default"
                  >
                    <span className="text-xl md:text-2xl font-bold text-[#f8f9fa] mb-1">{stat.number}</span>
                    <p className="text-xs md:text-sm text-[#dee2e6] font-medium whitespace-nowrap">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

    </section>
  );
};

export default HOACommercialOverview;